import { ReactNode } from "react";
import { styled } from "styled-components";
import { MdCheck, MdClose, MdWarning } from "react-icons/md";
import { colors } from "../../../theme/colors";
import { card } from "./section-card.component";

export type StepNavItem = {
  title: string;
  description?: string;
};

export type StepCompletionStatus = boolean | null | "warning";

type StepState = "future" | "active" | "complete" | "warning" | "error";

const Shell = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1.4rem 1.2rem;
  background-color: ${card.bg};
  border: 1px solid ${card.border};
  border-radius: 1.2rem;
  box-shadow: ${card.shadow};
`;

const Item = styled.button<{ $active: boolean }>`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 1.2rem;
  width: 100%;
  padding: 1rem 0.8rem;
  background: ${({ $active }) => ($active ? card.accentSoft : "transparent")};
  border: none;
  border-radius: 0.8rem;
  text-align: left;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: ${({ $active }) =>
      $active ? card.accentHover : card.accentSoft};
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary.hex};
    outline-offset: 2px;
  }
`;

const Connector = styled.span<{ $tone: "default" | "complete" | "warning" }>`
  position: absolute;
  left: calc(0.8rem + 1.55rem);
  top: 4.4rem;
  bottom: -0.4rem;
  width: 0.2rem;
  background: ${({ $tone }) =>
    $tone === "complete"
      ? colors.primary.hex
      : $tone === "warning"
        ? colors.warning.hex
        : card.border};
  border-radius: 0.2rem;
  z-index: 0;
  transition: background 0.2s;
`;

const Indicator = styled.span<{ $state: StepState }>`
  flex: 0 0 auto;
  width: 3.2rem;
  height: 3.2rem;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.35rem;
  font-variant-numeric: tabular-nums;
  position: relative;
  z-index: 1;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;

  ${({ $state }) => {
    switch ($state) {
      case "complete":
        return `
          background: ${colors.primary.hex};
          color: ${colors.white.hex};
        `;
      case "active":
        return `
          background: ${card.innerBg};
          color: ${colors.primary.hex};
          box-shadow:
            inset 0 0 0 2px ${colors.primary.hex},
            0 0 0 4px ${card.accentSoft};
        `;
      case "warning":
        return `
          background: ${colors.warning.hex};
          color: ${colors.white.hex};
        `;
      case "error":
        return `
          background: ${colors.error.hex};
          color: ${colors.white.hex};
        `;
      default:
        return `
          background: ${card.innerBg};
          color: ${colors.text.hex};
          opacity: 0.65;
          box-shadow: inset 0 0 0 1px ${card.border};
        `;
    }
  }}

  & svg {
    font-size: 1.7rem;
  }
`;

const TextWrap = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding-top: 0.55rem;
`;

const Title = styled.span<{ $state: StepState }>`
  font-size: 1.35rem;
  line-height: 1.3;
  letter-spacing: -0.01em;
  font-weight: ${({ $state }) =>
    $state === "active" ? 600 : 500};
  color: ${({ $state }) =>
    $state === "error"
      ? colors.error.hex
      : $state === "warning"
        ? colors.warning.hex
        : colors.text.hex};
  opacity: ${({ $state }) => ($state === "future" ? 0.65 : 1)};
`;

const Description = styled.span`
  font-size: 1.15rem;
  color: ${colors.text.hex};
  opacity: 0.55;
  line-height: 1.45;
`;

const renderIndicator = (state: StepState, index: number): ReactNode => {
  if (state === "complete") return <MdCheck />;
  if (state === "warning") return <MdWarning />;
  if (state === "error") return <MdClose />;
  return index + 1;
};

export const StepNav = ({
  items,
  current,
  completed,
  onChange,
}: {
  items: StepNavItem[];
  current: number;
  completed: StepCompletionStatus[];
  onChange: (index: number) => void;
}) => {
  return (
    <Shell>
      {items.map((item, index) => {
        const status = completed[index];
        const isActive = current === index;
        const isComplete = status === true;
        const hasWarning = status === "warning";
        const hasError = status === false;
        const state: StepState = isActive
          ? "active"
          : isComplete
          ? "complete"
          : hasWarning
          ? "warning"
          : hasError
          ? "error"
          : "future";

        const isLast = index === items.length - 1;
        const connectorTone = isComplete
          ? "complete"
          : hasWarning
            ? "warning"
            : "default";

        return (
          <Item
            key={item.title}
            type="button"
            $active={isActive}
            onClick={() => onChange(index)}
          >
            {!isLast ? <Connector $tone={connectorTone} /> : null}
            <Indicator $state={state}>
              {renderIndicator(state, index)}
            </Indicator>
            <TextWrap>
              <Title $state={state}>{item.title}</Title>
              {item.description ? (
                <Description>{item.description}</Description>
              ) : null}
            </TextWrap>
          </Item>
        );
      })}
    </Shell>
  );
};
